import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { I18nHelperService } from '@xref/ngx';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { IdService } from '../id/id.service';
import { PermissionsService } from '../permissions/permissions.service';

// Global DevCycle flag gating the Enterprise (XSP) password-policy + MFA work.
// Evaluated at the environment level so it resolves before the user logs in.
export const MFA_FEATURE_FLAG = 'ent-10313-password-policy-and-mfa-global';

interface TwoFactorVerifyRequest {
    mfa_code: string;
}

interface SmsSetupRequest {
    phone_number: string;
    country_code: string;
}

export interface TwoFactorOverviewResponse {
    mfa_enabled: boolean;
    // The configured method once MFA is enabled: 'sms' | 'email' | 'app'.
    challenge_type?: string;
    obfuscated_destination?: string;
    // Enterprise (XSP) acceptance flags, present only when is_xref_login is sent
    // and the user is an Enterprise user.
    mfa_app_accepted?: boolean;
    mfa_sms_accepted?: boolean;
    mfa_email_accepted?: boolean;
    // Legacy (Recruiter/Admin) overview fields.
    email_allowed?: boolean;
    mobile_allowed?: boolean;
    user_choice?: string;
}

// Enterprise (XSP) challenge types, mirrors id.xref.io multi_factor constants.
export const CHALLENGE_TYPE = {
    SMS: 'sms',
    EMAIL: 'email',
    APP: 'app'
};

@Injectable()
export class TwoFactorService {
    data: TwoFactorOverviewResponse;
    dataUpdated$ = new Subject();

    baseUrl = `${this.idService.url}multi-factor/`;

    smsModel: SmsSetupRequest = {
        phone_number: '',
        country_code: ''
    };

    constructor(
        private http: HttpClient,
        private idService: IdService,
        private i18nHelper: I18nHelperService,
        private permissions: PermissionsService
    ) {}

    reduce(data: TwoFactorOverviewResponse): TwoFactorOverviewResponse {
        const reducedData: any = {};
        Object.assign(reducedData, data);
        return reducedData;
    }

    hasData(): boolean {
        return this.data && this.data.hasOwnProperty('mfa_enabled');
    }

    /**
     * True when the Enterprise (XSP) MFA flow should be used. Requires both the
     * global `ent-10313-password-policy-and-mfa` flag to be active and the
     * overview response to carry the Enterprise-only `mfa_app_accepted` key
     * (returned by id.xref.io when `is_xref_login=true` for an XSP user).
     */
    isEnterprise(): boolean {
        return this.permissions.isFeatureActivated(MFA_FEATURE_FLAG)
            && !!this.data && this.data.hasOwnProperty('mfa_app_accepted');
    }

    // @success <optional>: A success method parsed from the caller to be executed in that context
    // @error <optional>: An error method parsed from the caller to be executed in that context
    getConfig(token: string, success: any = () => {}, error: any = () => {}, force = false): void {
    // Take the success parsed into the function, perform local logic first then parsed success
        const successWrapped = (data: any) => {
            this.data = this.reduce(data.data);
            this.dataUpdated$.next(true);
            success(this.data);
        };

        // Take the error parsed into the function, perform local logic first then parsed success
        const errorWrapped = (err: any) => {
            error(err);
        };

        if (!this.hasData() || force) {
            // `is_xref_login=true` asks id.xref.io to return the Enterprise (XSP)
            // MFA configuration (challenge_type + acceptance flags) for XSP users.
            // Only request it when the global flag is on so the flag is a complete
            // rollback switch for the Enterprise contract.
            const overviewUrl = this.permissions.isFeatureActivated(MFA_FEATURE_FLAG)
                ? `${this.baseUrl}overview?is_xref_login=true`
                : `${this.baseUrl}overview`;

            this.http
                .get(overviewUrl, this.createAuthOptions(token))
                .subscribe(successWrapped, errorWrapped);
        } else {
            success(this.data);
        }
    }

    requestCode(token: string): Observable<any> {
        return this.http.put(
            this.withXrefLogin(`${this.baseUrl}request`),
            { locale: this.i18nHelper.locale },
            this.createAuthOptions(token)
        ).pipe(map(res => res));
    }

    verifyCode(token: string, data: TwoFactorVerifyRequest): Observable<any> {
        return this.http.put(
            this.withXrefLogin(`${this.baseUrl}verify`),
            data,
            this.createAuthOptions(token)
        ).pipe(map(res => res));
    }

    /**
     * Enterprise (XSP) SMS setup: send an OTP to the user's verified mobile.
     * The phone number is resolved server-side; no client input is required.
     */
    requestXspSmsSetupCode(token: string): Observable<any> {
        return this.http.put(
            `${this.baseUrl}sms/xsp/request`,
            { locale: this.i18nHelper.locale },
            this.createAuthOptions(token)
        ).pipe(map(res => res));
    }

    /**
     * Enterprise (XSP) SMS setup: verify the OTP and enable SMS as the MFA
     * method. On success id.xref.io mints login tokens.
     */
    verifyXspSmsSetupCode(token: string, data: TwoFactorVerifyRequest): Observable<any> {
        return this.http.put(
            `${this.baseUrl}sms/xsp/verify`,
            data,
            this.createAuthOptions(token)
        ).pipe(map(res => res));
    }

    /**
     * Enterprise (XSP) authenticator app setup: returns a `provisioning_uri`
     * used to render the QR code the user scans.
     */
    requestXspAppProvisioningUri(token: string): Observable<any> {
        return this.http.put(
            `${this.baseUrl}app/request`,
            {},
            this.createAuthOptions(token)
        ).pipe(map(res => res));
    }

    /**
     * Enterprise (XSP) authenticator app setup: verify the TOTP code and enable
     * the authenticator app as the MFA method. On success login tokens are minted.
     */
    verifyXspAppSetupCode(token: string, data: TwoFactorVerifyRequest): Observable<any> {
        return this.http.put(
            `${this.baseUrl}app/verify`,
            data,
            this.createAuthOptions(token)
        ).pipe(map(res => res));
    }

    requestEmailVerificationCode(token: string): Observable<any> {
        return this.http.put(
            `${this.baseUrl}email/request`,
            { locale: this.i18nHelper.locale },
            this.createAuthOptions(token)
        ).pipe(map(res => res));
    }

    verifyEmailVerificationCode(token: string, data: TwoFactorVerifyRequest): Observable<any> {
        return this.http.put(
            `${this.baseUrl}email/verify`,
            data,
            this.createAuthOptions(token)
        ).pipe(map(res => res));
    }

    requestSmsVerificationCode(token: string, data: SmsSetupRequest): Observable<any> {
        const payload = Object.assign({ locale: this.i18nHelper.locale }, data);

        payload.country_code = payload.country_code.slice(1);

        return this.http.put(
            `${this.baseUrl}sms/request`,
            payload,
            this.createAuthOptions(token)
        ).pipe(map(res => res));
    }

    verifySmsVerificationCode(token: string, data: TwoFactorVerifyRequest): Observable<any> {
        return this.http.put(
            `${this.baseUrl}sms/verify`,
            data,
            this.createAuthOptions(token)
        ).pipe(map(res => res));
    }

    /**
     * Append `is_xref_login=true` to a login-time MFA endpoint when the global
     * flag is on, so id.xref.io routes the request down the Enterprise (XSP) path
     * and mints an 'xsp' token (redirecting back to the Enterprise app). Mirrors
     * `getConfig`; without it admins/legacy users on the Enterprise login flow get
     * the wrong config and an 'xrp' token. The flag keeps this a full rollback switch.
     */
    private withXrefLogin(url: string): string {
        return this.permissions.isFeatureActivated(MFA_FEATURE_FLAG)
            ? `${url}?is_xref_login=true`
            : url;
    }

    private createAuthOptions(token: string) {
        return {headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })};
    }
}
